/**
 * Extranet route table, sidebar items and page titles — declarative config
 * with stable keys (contract §9). The module is mounted at
 * `/extranet/:tenantSlug/*` (`src/App.tsx`); every path below is relative to
 * that root and built with {@link extranetPath}.
 */

import {
  AlertTriangle,
  Car,
  FileText,
  Home,
  Inbox,
  Receipt,
  type LucideIcon,
} from 'lucide-react';

/** Relative route patterns of the module (React Router `path`s). */
export const EXTRANET_ROUTES = {
  home: '/',
  contracts: '/contrats',
  contract: '/contrats/:idNpol',
  claims: '/sinistres',
  claim: '/sinistres/:idSin',
  premiums: '/primes',
  fleet: '/flotte',
  vehicle: '/flotte/:idNpol/:idVeh',
  pieces: '/pieces-attendues',
} as const;

/** Stable key of a sidebar entry. */
export type ExtranetNavKey =
  | 'accueil'
  | 'contrats'
  | 'sinistres'
  | 'primes'
  | 'flotte'
  | 'pieces';

/** One sidebar entry. */
export interface ExtranetNavItem {
  /** Stable key (never the label). */
  key: ExtranetNavKey;
  /** Display label. */
  label: string;
  icon: LucideIcon;
  /** Sub-path under `/extranet/:tenantSlug` (`''` = accueil). */
  path: string;
  /** Active only on an exact match (accueil); otherwise also on sub-paths. */
  exact?: boolean;
}

/** The six sidebar entries of the design, in order. */
export const EXTRANET_NAV_ITEMS: readonly ExtranetNavItem[] = [
  { key: 'accueil', label: 'Accueil', icon: Home, path: '', exact: true },
  { key: 'contrats', label: 'Contrats', icon: FileText, path: 'contrats' },
  { key: 'sinistres', label: 'Sinistres', icon: AlertTriangle, path: 'sinistres' },
  { key: 'primes', label: 'Primes', icon: Receipt, path: 'primes' },
  { key: 'flotte', label: 'Flotte auto', icon: Car, path: 'flotte' },
  { key: 'pieces', label: 'Pièces à fournir', icon: Inbox, path: 'pieces-attendues' },
];

/**
 * Absolute path of a module screen.
 *
 * @example extranetPath('eurosud', 'contrats', 7015) // '/extranet/eurosud/contrats/7015'
 */
export function extranetPath(
  tenantSlug: string | undefined,
  ...segments: ReadonlyArray<string | number>
): string {
  const root = `/extranet/${encodeURIComponent(tenantSlug ?? '')}`;
  const tail = segments
    .map((segment) => String(segment))
    .filter((segment) => segment !== '')
    .map((segment) => (segment.includes('/') ? segment : encodeURIComponent(segment)))
    .join('/');
  return tail === '' ? root : `${root}/${tail}`;
}

/** `true` when `pathname` is the screen of `item` (or one of its sub-screens). */
export function isNavItemActive(
  item: ExtranetNavItem,
  pathname: string,
  tenantSlug: string | undefined,
): boolean {
  const target = extranetPath(tenantSlug, item.path);
  const normalised = pathname.replace(/\/+$/, '') || '/';
  if (item.exact) return normalised === target;
  return normalised === target || normalised.startsWith(`${target}/`);
}

/**
 * Page header texts of the screens (the accueil renders its own welcome
 * header — `components/home/homeText.ts`). `title` of a detail screen is the
 * fallback while its record loads.
 */
export const EXTRANET_PAGE_TEXT = {
  contracts: { title: 'Mes contrats', breadcrumb: 'Contrats' },
  contract: { title: 'Contrat', breadcrumb: 'Contrats' },
  claims: { title: 'Mes sinistres', breadcrumb: 'Sinistres' },
  claim: { title: 'Sinistre', breadcrumb: 'Sinistres' },
  premiums: { title: 'Mes primes', breadcrumb: 'Primes' },
  fleet: { title: 'Flotte automobile', breadcrumb: 'Flotte auto' },
  vehicle: { title: 'Véhicule', breadcrumb: 'Flotte auto' },
  pieces: { title: 'Pièces à fournir', breadcrumb: 'Pièces à fournir' },
} as const;
